import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { ClosedPeriodResponse } from "../contracts/ClosedPeriodResponse";
import { CreateClosedPeriodRequest } from "../contracts/CreateClosedPeriodRequest";
import { PaginatedClosedPeriodResult } from "../contracts/PaginatedClosedPeriodResult";
import { UpdateClosedPeriodRequest } from "../contracts/UpdateClosedPeriodRequest";
import { mapResponseToClosedPeriod } from "../mappers/ClosedPeriodMapper";
import { ClosedPeriod } from "../models/ClosedPeriod";

const BASE = "/closedperiods";

export interface FetchClosedPeriodsArgs {
  memberId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export interface PaginatedClosedPeriods {
  items: ClosedPeriod[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export const fetchClosedPeriods = createAsyncThunk<
  PaginatedClosedPeriods,
  FetchClosedPeriodsArgs | void,
  { rejectValue: ApiErrorResponse }
>("closedPeriods/fetchAll", async (args, { rejectWithValue }) => {
  try {
    const params: Record<string, string | number> = {
      page: (args && args.page) ?? 1,
      pageSize: (args && args.pageSize) ?? 20,
    };
    if (args?.memberId) params.memberId = args.memberId;
    if (args?.from) params.from = args.from;
    if (args?.to) params.to = args.to;

    const response = await axiosInstance.get<PaginatedClosedPeriodResult>(BASE, { params });
    return {
      items: response.data.items.map(mapResponseToClosedPeriod),
      page: response.data.page,
      pageSize: response.data.pageSize,
      totalCount: response.data.totalCount,
      totalPages: response.data.totalPages,
      hasPreviousPage: response.data.hasPreviousPage,
      hasNextPage: response.data.hasNextPage,
    };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load closed periods"));
  }
});

export const createClosedPeriod = createAsyncThunk<
  ClosedPeriod,
  CreateClosedPeriodRequest,
  { rejectValue: ApiErrorResponse }
>("closedPeriods/create", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ClosedPeriodResponse>(BASE, request);
    return mapResponseToClosedPeriod(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to create closed period"));
  }
});

export const updateClosedPeriod = createAsyncThunk<
  ClosedPeriod,
  { id: string; request: UpdateClosedPeriodRequest },
  { rejectValue: ApiErrorResponse }
>("closedPeriods/update", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<ClosedPeriodResponse>(`${BASE}/${id}`, request);
    return mapResponseToClosedPeriod(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to update closed period"));
  }
});

export const deleteClosedPeriod = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("closedPeriods/delete", async (id, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`${BASE}/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to delete closed period"));
  }
});
